import type { User } from "firebase/auth";

/**
 * Build authentication headers for API requests to the backend.
 *
 * Includes both the legacy X-Sc-Auth-* headers for backward compatibility
 * and the new Authorization header with Firebase ID token for proper authentication.
 *
 * @param user - Firebase user object
 * @returns Promise resolving to auth headers object
 */
export async function buildAuthHeaders(user: User): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "X-Sc-Auth-Sub": user.uid,
    "X-Sc-Auth-Provider": user.providerData?.[0]?.providerId ?? user.providerId ?? "firebase",
  };

  if (user.email) {
    headers["X-Sc-Auth-Email"] = user.email;
  }
  if (user.displayName) {
    headers["X-Sc-Auth-Name"] = user.displayName;
  }
  if (user.photoURL) {
    headers["X-Sc-Auth-Avatar"] = user.photoURL;
  }

  // Get Firebase ID token for proper authentication
  if (typeof user.getIdToken === "function") {
    try {
      const token = await user.getIdToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn("Failed to get ID token for authentication:", error);
    }
  }

  return headers;
}

/**
 * Build authentication headers synchronously for cases where async is not possible.
 * Note: This will not include the Firebase ID token, only legacy headers.
 * Use buildAuthHeaders() whenever possible for full authentication.
 *
 * @param user - Firebase user object
 * @returns Auth headers object (without Authorization token)
 */
export function buildAuthHeadersSync(user: User): Record<string, string> {
  const headers: Record<string, string> = {
    "X-Sc-Auth-Sub": user.uid,
    "X-Sc-Auth-Provider": user.providerData?.[0]?.providerId ?? user.providerId ?? "firebase",
  };

  if (user.email) {
    headers["X-Sc-Auth-Email"] = user.email;
  }
  if (user.displayName) {
    headers["X-Sc-Auth-Name"] = user.displayName;
  }
  if (user.photoURL) {
    headers["X-Sc-Auth-Avatar"] = user.photoURL;
  }

  return headers;
}
